import {
  AfterViewChecked,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Project, VersionInfo } from 'src/app/core/objects/project';
import { User } from 'src/app/core/objects/user';
import { NotificationsService } from 'src/app/core/services/notifications.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { InterlayerWritePayload, InterlayersApiService } from 'src/app/core/services/interlayers-api.service';
import { ColorsService } from '../../core/services/colors.service';
import { ProjectApiService } from '../../core/services/project-api.service';
import { UserService } from '../../core/services/user.service';
import {
  CargoSpace,
  Dimensions,
  InterlayerConfig,
  Load,
  LoadingPlacesOptions,
  LoadWithTranslatedName,
  PalletInterlayer,
  PalletLayer,
  PalletLayerGroup,
  PalletLayerGroupEdit,
  Position,
  RotateOptions,
  SavedInterlayer,
  StackingOptions,
} from '../../core/types';
import { DISABLED_INITIAL_ANIMATION, WIDTH_ANIMATION } from '../../shared/animations/animations';
import { LoadDimensionsComponent } from './components/load-dimensions/load-dimensions.component';
import { LoadInterlayersComponent } from './components/load-interlayers/load-interlayers.component';
import { LoadPalletArrangementComponent } from './components/load-pallet-arrangement/load-pallet-arrangement.component';
import { ModalService } from '../../modal/modal.service';
import { MaxLoadsPopupComponent } from '../../modal/components/maxLoadsPopup/max-loads-popup.component';
import { ProInfoComponent } from '../../modal/components/pro-info/pro-info.component';
import { SharedChangesEditPopupComponent } from '../../container/shared-changes-edit-popup/shared-changes-edit-popup.component';
import { PaymentApiService } from '../../core/services/payment-api.service';
import {
  getFromLocalStorage,
  isMoreThanDaysAgo,
  setLocalStorage,
} from '../utils/localStorageHelpers';
import {
  convertLoadDimensions,
  convertLoadsDimensions,
} from '../../shared/helpers/unitRecalculationHelpers';
import {
  cargoSpaceHasIndividualLoads,
  cargoSpaceHasLoadedPalletLayers,
  getActivePalletLayerGroups,
  getArrangementLayersFromGroups,
  getVisualizationCargoSpaces,
  isActivePalletLoadingSpace,
  resolveMatchingPalletLayerGroup,
  resolveSingleLayerCapacity,
  supportsPalletLayerLoadType,
} from '../../shared/helpers/pallet-loading-space.helper';
import {
  buildPalletLayerGroupEditRequest,
  interlayerConfigToPalletInterlayer,
  resolveInterlayerTemplateMeta,
} from '../../shared/helpers/pallet-layer-add.helper';
import { resolveInterlayerDisplayName } from '../../shared/helpers/pallet-interlayer-name.helper';

export interface FormDataType {
  color: number;
  height: number;
  length: number;
  name: string;
  nameTranslated: string;
  quantity: number;
  sumOfWeights: number;
  weight: number;
  width: number;
  loadType: number;
  centerOfMass?: Position;
  diameter: number;
}

@Component({
    selector: 'gl-add-load',
    templateUrl: './add-load.component.html',
    styleUrls: ['./add-load.component.scss'],
    animations: [WIDTH_ANIMATION, DISABLED_INITIAL_ANIMATION],
    standalone: false
})
export class AddLoadComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() isQuick = false;
  @Output() public closeAddLoad = new EventEmitter();
  public loads: LoadWithTranslatedName[];
  @ViewChild('dimensions', { static: true })
  public dimensionsComponent: LoadDimensionsComponent;
  @ViewChild('interlayersRef', { static: false })
  private interlayersRef?: LoadInterlayersComponent;
  @ViewChild(LoadPalletArrangementComponent, { static: false })
  private arrangementRef?: LoadPalletArrangementComponent;
  public project: Project;
  public user: User;
  public isSortByNameEnabled: boolean;
  public currentLoad: Load = this.getDefaultLoad();
  private savedLoad: Load; // Store saved load for restore functionality
  public isEdit = false;
  public isTabPreview: boolean;
  public isTabStacking: boolean;
  public isTabGravityOfCenter: boolean;
  public isTabLoadingPlaces: boolean;
  public stackingOptions: StackingOptions;
  public isTabRotate: boolean;
  public isTabInterlayers: boolean;
  public isTabArrangement: boolean;
  /** Layers on the pallet (loadType 0 only); independent of line quantity */
  public palletLayerCount = 1;
  /** Layer arrangement options from the latest calculate response */
  public arrangementLayers: PalletLayer[] = [];
  public selectedPalletLayerId: number | null = null;
  /** Per arrangement option: how many pallet layers to add on Add */
  public arrangementLayerCounts: Record<number, number> = {};
  /** Pallet layer group from the latest calculate — required for layerGroup/edit */
  public arrangementLayerGroupId: number | null = null;
  /** When true, line quantity is driven by layer flow instead of free-form entry */
  public quantityLockedForLayers = false;
  public highlightAddButton = false;
  /** Cached bindings for project-preview — avoid new object refs every change detection. */
  public arrangementPreviewInterlayer: PalletInterlayer | null = null;
  public pendingLoadDimensionsForPreview?: Dimensions;
  public interlayerSavedLoads: LoadWithTranslatedName[] = [];
  private arrangementPreviewInterlayerSignature = '';
  private pendingLoadDimensionsSignature = '';
  private calculatedLoadSignature = '';
  private interlayersPreviewSyncSubscribed = false;
  private interlayersPreviewFormSubscription?: Subscription;
  public rotateOptions: RotateOptions = {
    rotateOnBase: true,
    rotateFreely: null,
    allowToRotate: null,
  };
  public allowOutOfSpace: boolean;
  public loadingPlacesOptions: LoadingPlacesOptions;
  public centerOfMass: Position;
  public centerOfMassValue: Position;
  public loadData: FormDataType = this.getDefaultLoadData();
  public loadDataVis: FormDataType;
  private sortedLoads: Load[];
  private subscriptions: Subscription[] = [];
  private nameToSend: string;
  public currentWorkSpace: number;
  public currentStopIndex: number;
  public isCreatingNewLoad: boolean;
  public currentType: number;
  private isQuickMode = false;
  public isListVisible = true;
  public isLoadDimensionsActive = true;
  public isSharedProjectLast = true;
  public currentSearchString = '';

  constructor(
    private projectService: ProjectService,
    private colorsService: ColorsService,
    private projectApiService: ProjectApiService,
    private userService: UserService,
    private userApiService: UserApiService,
    private interlayersApi: InterlayersApiService,
    private translate: TranslateService,
    private cookieService: CookieService,
    private notificationsService: NotificationsService,
    private modalService: ModalService,
    private paymentApiService: PaymentApiService,
  ) {
    this.isSortByNameEnabled = this.cookieService.get('sortByName').includes('true') ? true : false;
    this.projectService.currentWorkSpaceIndex.subscribe((currentWorkSpace: number) => {
      this.currentWorkSpace = currentWorkSpace;
      this.leaveLayerIncompatibleTabsIfNeeded();
      this.syncArrangementPreviewState();
    });
    this.projectService.currentStopIndex.subscribe((currentStopIndex: number) => {
      this.currentStopIndex = currentStopIndex;
      this.leaveLayerIncompatibleTabsIfNeeded();
      this.syncArrangementPreviewState();
    });
    this.projectService.project.subscribe((project: Project) => {
      this.project = project;
      this.leaveLayerIncompatibleTabsIfNeeded();
      this.userService.user.subscribe((user: User) => {
        this.user = user;
        user.loads?.user.forEach((load) => {
          load.isUser = true;
        });
        const userLoads: Load[] = user.loads?.user;
        this.sortedLoads = this.userService.sortLoadsByVolume(
          user.loads?.standard.concat(userLoads),
        );
        const isMm = project.options.unit === 'mm';
        if (isMm) {
          this.sortedLoads = convertLoadsDimensions(this.sortedLoads, true);
        }
        this.loadLoads();
      });
    });
  }

  public ngOnInit(): void {
    this.getProjectVersions();
    this.isQuickMode = this.isQuick;
    this.subscriptions.push(
      this.dimensionsComponent.form.controls.load.controls.name.valueChanges.subscribe(
        (value: string) => (this.nameToSend = value),
      ),
    );
  }

  public previewClick(): void {
    this.closeAddLoad.emit();
  }

  public ngOnDestroy(): void {
    this.interlayersPreviewFormSubscription?.unsubscribe();
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public ngAfterViewChecked(): void {
    this.ensureInterlayersPreviewSync();
  }

  private ensureInterlayersPreviewSync(): void {
    if (!this.interlayersRef) {
      this.interlayersPreviewFormSubscription?.unsubscribe();
      this.interlayersPreviewFormSubscription = undefined;
      this.interlayersPreviewSyncSubscribed = false;
      return;
    }
    if (this.interlayersPreviewSyncSubscribed) {
      return;
    }
    this.interlayersPreviewSyncSubscribed = true;
    this.interlayersPreviewFormSubscription = this.interlayersRef.form.valueChanges.subscribe(() =>
      this.syncArrangementPreviewState(),
    );
    this.syncArrangementPreviewState();
  }

  public listVisibleChanger(): void {
    this.isListVisible = !this.isListVisible;
  }

  public close(): void {
    this.closeAddLoad.emit();
  }

  public enableEdit(): void {
    if (this.currentLoad && this.dimensionsComponent?.form?.disabled) {
      this.isEdit = true;
      this.dimensionsComponent.enableFormSafely();
    }
  }

  public onChosen(load: Load, disable = true): void {
    this.stackingOptions = { ...load.stackingOptions, stacking: load.stacking };
    this.isCreatingNewLoad = false;
    this.currentLoad = { ...load };
    // Save the load for restore functionality
    this.savedLoad = { ...load };
    load.quantity = 1;
    this.nameToSend = load.name;
    this.currentType = load.loadType;
    this.palletLayerCount =
      load.loadType === 0 &&
      typeof load.palletLayerCount === 'number' &&
      load.palletLayerCount >= 1
        ? load.palletLayerCount
        : 1;
    queueMicrotask(() => this.interlayersRef?.reset());
    this.centerOfMass = load.centerOfMass;
    this.rotateOptions = {
      rotateOnBase: load.rotateOnBase,
      rotateFreely: load.rotateFreely,
      allowToRotate: load.allowToRotate,
    };
    if (disable) {
      this.dimensionsComponent.form.disable();
    }

    if (!this.isTabPreview) {
      this.openTab('');
    }
  }

  public setStackingOptions(stackingOptions: StackingOptions): void {
    this.stackingOptions = {
      ...stackingOptions,
      maxLoadsOnTop: stackingOptions?.maxLoadsOnTop === '0' ? null : stackingOptions?.maxLoadsOnTop,
      formation: this.stackingOptions?.formation,
    };
  }

  public setFormationStackingOptions(stackingOptions: StackingOptions): void {
    if (
      this.loadData.loadType === 2 &&
      (stackingOptions?.formation === 1 || stackingOptions?.formation === 2)
    ) {
      stackingOptions.stacking = true;
    } else if (this.loadData.loadType === 2 && stackingOptions?.formation === 0) {
      stackingOptions.stacking = false;
    }
    this.stackingOptions = {
      ...this.stackingOptions,
      formation: stackingOptions.formation,
    };
  }

  public onRotateOptionsChange(rotateOptions: RotateOptions): void {
    const previousMode = this.resolveRotateMode(this.rotateOptions);
    this.rotateOptions = rotateOptions;
    const nextMode = this.resolveRotateMode(rotateOptions);
    if (previousMode === nextMode || !this.arrangementLayers.length) {
      return;
    }
    this.dimensionsComponent.enableFormSafely();
    if (!this.hasValidLayerCalculateDimensions()) {
      return;
    }
    this.runPalletLayerCalculate({ adjustLayerCountOnFailure: false });
  }

  /** Effective rotation choice for compare — ignores null vs false noise from the rotate form. */
  private resolveRotateMode(
    options: RotateOptions | null | undefined,
  ): 'rotateOnBase' | 'rotateFreely' | 'dontAllow' {
    if (options?.rotateFreely) {
      return 'rotateFreely';
    }
    if (options?.rotateOnBase) {
      return 'rotateOnBase';
    }
    // Emitted "don't allow" is allowToRotate === false with other flags null;
    // initial state also has allowToRotate: false but with rotateOnBase: true (handled above).
    if (options?.allowToRotate === false) {
      return 'dontAllow';
    }
    return 'rotateOnBase';
  }

  public setAllowOutOfSpace(allowOutOfSpace: boolean): void {
    this.allowOutOfSpace = allowOutOfSpace;
  }

  public setLoadingPlacesOptions(loadingPlacesOptions: LoadingPlacesOptions): void {
    this.loadingPlacesOptions = loadingPlacesOptions;
  }

  public setCenterOfMass(centerOfMass: Position): void {
    this.centerOfMassValue = centerOfMass;
  }

  public isCurrentMultiStopWorkSpace(): boolean {
    return this.project.workSpaces[this.currentWorkSpace]?.stops?.length > 0 ? true : false;
  }

  public openLoadType(type: number): void {
    this.createLoad(type);
  }

  public createLoad(kind: number): void {
    this.resetForm();
    this.savedLoad = undefined; // Clear saved load when creating new load
    this.openTab('');
    this.currentType = undefined;
    this.isCreatingNewLoad = true;
    this.isListVisible = false;
    this.chooseType(kind);
  }

  public chooseType(num: number): void {
    this.isCreatingNewLoad = false;
    this.currentType = num;
    if (num !== 0) {
      this.palletLayerCount = 1;
      this.clearArrangementState();
    }
    this.currentLoad = this.getDefaultLoad();
    this.loadData = convertLoadDimensions(this.getDefaultLoadData(), this.project.options.unit);
    this.loadDataVis = this.loadData;
  }

  public onAddLoad(addedLoad: Load): void {
    this.changeTabsActivityOnSubmit();
    this.currentLoad = addedLoad;
    this.loads = this.loads.map((load: Load) => {
      if (load.id === addedLoad.id) {
        const color: number = load.color;
        return {
          ...load,
          color,
          translatedName: this.translateLoadName(load.name),
        };
      } else {
        return {
          ...load,
          translatedName: this.translateLoadName(load.name),
        };
      }
    });
  }

  private changeTabsActivityOnSubmit(): void {
    this.isTabStacking = false;
    this.isTabRotate = false;
    this.isTabGravityOfCenter = false;
    this.isTabLoadingPlaces = false;
    this.isTabInterlayers = false;
    this.isTabArrangement = false;
    this.isTabPreview = true;
    this.clearArrangementState();
  }

  public get showArrangementTab(): boolean {
    return this.canUsePalletLayerFeatures() && this.arrangementLayers.length > 0;
  }

  public isLayerCalculationStale(): boolean {
    if (!this.arrangementLayers.length || !this.dimensionsComponent) {
      return false;
    }
    return this.buildDimensionsSignature(this.getLoadFormValue()) !== this.calculatedLoadSignature;
  }

  private buildDimensionsSignature(source: {
    length?: number;
    width?: number;
    height?: number;
    diameter?: number;
    weight?: number;
  }): string {
    return [source.length, source.width, source.height, source.diameter, source.weight]
      .map((value) => value ?? '')
      .join('|');
  }

  /** Layer count used by interlayers tab and add payload when arrangement flow is active. */
  public get effectivePalletLayerCount(): number {
    if (this.showArrangementTab) {
      return Object.values(this.arrangementLayerCounts).reduce(
        (acc, count) => acc + (count > 0 ? count : 0),
        0,
      );
    }
    return 0;
  }

  private clearArrangementState(): void {
    this.arrangementLayers = [];
    this.selectedPalletLayerId = null;
    this.arrangementLayerCounts = {};
    this.arrangementLayerGroupId = null;
    this.quantityLockedForLayers = false;
    this.highlightAddButton = false;
    this.calculatedLoadSignature = '';
    this.syncArrangementPreviewState();
  }

  public supportsPalletLayerLoadType(loadType?: number): boolean {
    return supportsPalletLayerLoadType(loadType ?? this.currentType);
  }

  public showPalletLayerActions(): boolean {
    return (
      this.isPalletLoadingSpaceActive() &&
      this.supportsPalletLayerLoadType(this.currentType) &&
      !this.activeCargoSpaceHasIndividualLoads()
    );
  }

  public showFillPalletAction(): boolean {
    return this.showPalletLayerActions() && !this.activeCargoSpaceHasLoadedPalletLayers();
  }

  /** Block individual add UI until the user starts the layer flow on a pallet with loaded layers. */
  public isIndividualAddBlocked(): boolean {
    if (!this.isPalletLoadingSpaceActive() || !this.activeCargoSpaceHasLoadedPalletLayers()) {
      return false;
    }
    return !this.quantityLockedForLayers && !this.showArrangementTab;
  }

  public isQuantityInputLocked(): boolean {
    return this.quantityLockedForLayers || this.isIndividualAddBlocked();
  }

  public activeCargoSpaceHasLoadedPalletLayers(): boolean {
    return cargoSpaceHasLoadedPalletLayers(this.getActiveCargoSpace());
  }

  private activeCargoSpaceHasIndividualLoads(): boolean {
    return cargoSpaceHasIndividualLoads(this.getActiveCargoSpace());
  }

  private getActiveCargoSpace(): CargoSpace | undefined {
    return getVisualizationCargoSpaces(this.project, this.currentWorkSpace, this.currentStopIndex)[0];
  }

  private shouldBlockIndividualAddPath(): boolean {
    return this.isPalletLoadingSpaceActive() && this.activeCargoSpaceHasLoadedPalletLayers();
  }

  /** Individual add from the load list is never allowed on a pallet that already uses layers. */
  public isIndividualLoadListAddBlocked(): boolean {
    return this.shouldBlockIndividualAddPath();
  }

  private refreshInterlayerSavedLoads(): void {
    const fromLoads = (this.loads ?? []).filter((l) => l.stackingOptions?.interlayer);
    const fromUserInterlayers = (this.user?.loads?.interlayers ?? []).map((interlayer: SavedInterlayer) =>
      this.mapSavedInterlayerToLoad(interlayer),
    );
    const byId = new Map<string, LoadWithTranslatedName>();
    [...fromLoads, ...fromUserInterlayers].forEach((load) => {
      const key = String(load.id ?? load.name ?? '');
      if (!byId.has(key)) {
        byId.set(key, load);
      }
    });
    this.interlayerSavedLoads = Array.from(byId.values());
  }

  public isPalletLoadShape(): boolean {
    return supportsPalletLayerLoadType(this.loadData?.loadType);
  }

  public isPalletLoadingSpaceActive(): boolean {
    return isActivePalletLoadingSpace(this.project, this.currentWorkSpace, this.currentStopIndex);
  }

  private leaveLayerIncompatibleTabsIfNeeded(): void {
    if (
      (this.activeCargoSpaceHasLoadedPalletLayers() || this.quantityLockedForLayers) &&
      (this.isTabStacking || this.isTabGravityOfCenter)
    ) {
      this.openTab('preview');
      return;
    }
    if (!this.isPalletLoadingSpaceActive() && this.isTabInterlayers) {
      this.openTab('preview');
    }
  }

  public canUsePalletLayerFeatures(): boolean {
    const loadType = this.loadData?.loadType ?? this.currentType;
    return (
      this.isPalletLoadingSpaceActive() &&
      supportsPalletLayerLoadType(loadType) &&
      !!this.user?.isPremium
    );
  }

  public onInterlayersTabClick(): void {
    if (!this.canUsePalletLayerFeatures()) {
      this.openProInfoModal();
      return;
    }
    if (this.showArrangementTab) {
      this.openTab('interlayers');
    }
  }

  private openProInfoModal(): void {
    this.paymentApiService
      .getPayment(this.user?.company?.country)
      .subscribe(() => this.modalService.open(ProInfoComponent).subscribe());
  }

  public quickAddLoad(addedLoad: Load): void {
    if (this.shouldBlockIndividualAddPath()) {
      return;
    }
    const localStorageData: { data: string; date: string } = getFromLocalStorage('addLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('addLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result) => {
        setLocalStorage('true', 'addLoads');
        if (result.data === 'yes') {
          this.quickAddBody(addedLoad);
        } else {
          return;
        }
      });
    } else {
      this.quickAddBody(addedLoad);
    }
  }

  private quickAddBody(addedLoad: Load): void {
    this.stackingOptions = { ...addedLoad.stackingOptions };
    const isLoadLimitReached: boolean =
      this.projectService.currentNumberOfLoads + addedLoad.quantity >
      this.projectService.maxNumberOfLoads;
    if (isLoadLimitReached) {
      this.modalService.open(MaxLoadsPopupComponent, null).subscribe();
      return;
    }
    const currentWorkSpaceId: number = this.project.workSpaces[this.currentWorkSpace]
      ? this.project.workSpaces[this.currentWorkSpace].id
      : undefined;
    const addLoad: Partial<Load> = this.prepareAddLoad();
    addLoad.color = addedLoad.color ? addedLoad.color : this.colorsService.getColor();
    const isMm = this.project.options.unit === 'mm';
    this.onAddLoad(addLoad as unknown as Load);
    this.projectApiService
      .addGroup({
        currentWorkSpaceId: currentWorkSpaceId,
        newLoads: [
          {
            quantity: addedLoad.quantity,
            load: isMm ? convertLoadsDimensions([addLoad as unknown as Load], false)[0] : addLoad,
          },
        ],
        workSpaceId: currentWorkSpaceId,
        currentStopId:
          this.currentStopIndex >= 0
            ? this.project.workSpaces[this.currentWorkSpace]?.stops[this.currentStopIndex]?.id
            : undefined,
      })
      .subscribe((result): void => {
        this.notificationsService.generateCOGNotification(result);
        this.notificationsService.generateLoadNotDisplayedNotification(
          isMm ? convertLoadsDimensions([addLoad as unknown as Load], false)[0] : addLoad,
          result,
        );
        if (this.currentSearchString) {
          this.onSearch(this.currentSearchString);
        }
      });
  }

  private prepareAddLoad(): Partial<Load> {
    const loadFormValue = this.getLoadFormValue();
    const rotateMode = this.resolveRotateMode(this.rotateOptions);
    const addLoad: Partial<Load> = {
      loadType: this.currentType,
      length:
        this.currentType === 1
          ? loadFormValue.diameter
          : loadFormValue.length,
      width:
        this.currentType === 1 || this.currentType === 2
          ? loadFormValue.diameter
          : loadFormValue.width,
      height:
        this.currentType === 2
          ? loadFormValue.diameter
          : loadFormValue.height,
      diameter:
        loadFormValue.diameter === 0
          ? undefined
          : loadFormValue.diameter,
      stacking: this.stackingOptions ? this.stackingOptions.stacking : false,
      name: this.nameToSend ? this.nameToSend : 'load_without_name',
      weight: loadFormValue.weight
        ? loadFormValue.weight
        : 0,
      rotateOnBase: rotateMode === 'rotateOnBase' ? true : undefined,
      rotateFreely: rotateMode === 'rotateFreely' ? true : undefined,
      allowToRotate: rotateMode === 'dontAllow' ? false : undefined,
      allowOutOfSpace: this.allowOutOfSpace ? this.allowOutOfSpace : undefined,
      loadingPlaces: this.loadingPlacesOptions,
    };
    if (this.centerOfMassValue) {
      addLoad.centerOfMass = this.centerOfMassValue;
    }
    addLoad.stacking = this.stackingOptions?.stacking ? this.stackingOptions.stacking : false;
    addLoad.stackingOptions = {};
    if (this.stackingOptions?.stacking && this.stackingOptions?.maxLoadsOnTop) {
      addLoad.stackingOptions.maxLoadsOnTop = this.stackingOptions.maxLoadsOnTop;
      addLoad.stacking = true;
    }
    if (this.stackingOptions?.stacking) {
      addLoad.stacking = true;
    }

    if (this.stackingOptions?.formation && (this.currentType === 1 || this.currentType === 2)) {
      addLoad.stackingOptions.formation = this.stackingOptions.formation;
    }

    if (this.stackingOptions?.maxWeightOnTop && parseInt(`${this.stackingOptions?.maxWeightOnTop}`, 10) > 0) {
      addLoad.stackingOptions.maxWeightOnTop = this.stackingOptions.maxWeightOnTop;
      addLoad.stacking = true;
    }

    if (this.stackingOptions?.alongFloor) {
      addLoad.stackingOptions.alongFloor = this.stackingOptions.alongFloor;
      // addLoad.stacking = true; GL-3799
    }

    if (this.stackingOptions?.pctSupport) {
      addLoad.stackingOptions.pctSupport = this.stackingOptions.pctSupport;
    }
    if (!addLoad.stacking) {
      delete addLoad['stackingOptions'];
    }

    if (this.stackingOptions?.pctSupport && !addLoad.stacking) {
      addLoad.stackingOptions = {};
      addLoad.stackingOptions.pctSupport = this.stackingOptions.pctSupport;
    }

    if (
      this.stackingOptions?.boxesBelowAllowed &&
      this.stackingOptions?.boxesBelowAllowed.length > 0
    ) {
      addLoad.stackingOptions = addLoad.stackingOptions || {};
      addLoad.stackingOptions.boxesBelowAllowed = this.stackingOptions.boxesBelowAllowed;
    }

    if (this.stackingOptions?.formation && !addLoad.stacking) {
      if (!this.stackingOptions?.pctSupport) {
        addLoad.stackingOptions = {};
      }
      if (this.stackingOptions?.formation !== 0 && addLoad.loadType === 2) {
        addLoad.stacking = true;
      }
    }
    if (!addLoad.stackingOptions) {
      addLoad.stackingOptions = {};
    }
    addLoad.stackingOptions.formation =
      this.stackingOptions?.formation === undefined && addLoad.loadType === 2
        ? addLoad.stacking
          ? 2
          : 0
        : this.stackingOptions?.formation;
    return addLoad;
  }

  private buildPalletLayerGroupEditForRequest(
    interlayerCfg: InterlayerConfig | undefined,
    fallbackColor: number,
    currentWorkSpaceId: number | undefined,
  ): PalletLayerGroupEdit | undefined {
    if (
      !this.canUsePalletLayerFeatures() ||
      currentWorkSpaceId == null ||
      this.arrangementLayerGroupId == null
    ) {
      return undefined;
    }
    const layers = this.showArrangementTab
      ? Object.entries(this.arrangementLayerCounts)
          .map(([layerId, quantity]) => ({
            layerId: Number(layerId),
            quantityDifference: quantity,
          }))
          .filter((entry) => entry.quantityDifference > 0)
      : this.selectedPalletLayerId != null
        ? [{ layerId: this.selectedPalletLayerId, quantityDifference: this.palletLayerCount }]
        : [];
    if (!layers.length) {
      return undefined;
    }
    const templateLoad = this.interlayersRef?.getSelectedTemplateLoad();
    const { color, weight } = resolveInterlayerTemplateMeta(templateLoad, fallbackColor);
    const stopId =
      this.currentStopIndex >= 0
        ? this.project.workSpaces[this.currentWorkSpace]?.stops[this.currentStopIndex]?.id
        : undefined;
    return buildPalletLayerGroupEditRequest({
      currentWorkSpaceId,
      workSpaceId: currentWorkSpaceId,
      stopId,
      layerGroupId: this.arrangementLayerGroupId,
      layers,
      interlayer: interlayerCfg,
      interlayerColor: color,
      interlayerWeight: weight,
    });
  }

  private displaySharedChangesEditPopup(): Observable<any> {
    return this.modalService.open(SharedChangesEditPopupComponent);
  }

  private getProjectVersions(): void {
    this.projectApiService.getProjectVersions().subscribe((versions: VersionInfo[]): void => {
      if (versions === null) return;
      const versionsLength: number = versions.length;
      const currentIndex: number = versions.findIndex((version: VersionInfo) => version.isCurrent);
      this.isSharedProjectLast = versionsLength - 1 === currentIndex;
    });
  }

  public addLoads(toFirstEmpty?: boolean): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('addLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('addLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'addLoads');
        if (result.data === 'no') {
          return;
        } else {
          this.addLoadBody(toFirstEmpty);
        }
      });
    } else {
      this.addLoadBody(toFirstEmpty);
    }
  }

  public onLayerCreateRequest(): void {
    if (!this.canUsePalletLayerFeatures()) {
      this.openProInfoModal();
      return;
    }
    this.dimensionsComponent.enableFormSafely();
    const isRecalculate = this.arrangementLayers.length > 0;
    if (isRecalculate) {
      if (!this.hasValidLayerCalculateDimensions()) {
        return;
      }
      this.runPalletLayerCalculate({ adjustLayerCountOnFailure: false });
      return;
    }
    if (!this.isFormValid()) {
      return;
    }
    this.quantityLockedForLayers = true;
    this.leaveLayerIncompatibleTabsIfNeeded();
    this.palletLayerCount += 1;
    this.runPalletLayerCalculate();
  }

  public onFillPalletRequest(): void {
    if (!this.canUsePalletLayerFeatures()) {
      this.openProInfoModal();
      return;
    }
    if (!this.dimensionsComponent.form.disabled && !this.hasValidLayerCalculateDimensions()) {
      return;
    }
    const currentWorkSpaceId = this.project.workSpaces[this.currentWorkSpace]
      ? this.project.workSpaces[this.currentWorkSpace].id
      : undefined;
    if (currentWorkSpaceId == null) {
      return;
    }
    const stopId =
      this.currentStopIndex >= 0
        ? this.project.workSpaces[this.currentWorkSpace]?.stops[this.currentStopIndex]?.id
        : undefined;
    const loadFormValue = this.getLoadFormValue();
    let load = this.prepareAddLoad() as Load;
    if (this.project.options.unit === 'mm') {
      load = convertLoadsDimensions([load], false)[0];
    }
    load.color = loadFormValue.color ? loadFormValue.color : this.colorsService.getColor();
    this.projectApiService
      .fillPallet({
        currentWorkSpaceId,
        ...(stopId !== undefined ? { currentStopId: stopId } : {}),
        target: {
          workSpaceId: currentWorkSpaceId,
          ...(stopId !== undefined ? { stopId } : {}),
        },
        load,
      })
      .subscribe((project) => {
        if (!project) {
          return;
        }
        this.project = project;
        this.resetForm(true);
        this.closeAddLoad.emit();
      });
  }

  private runPalletLayerCalculate(options?: { adjustLayerCountOnFailure?: boolean }): void {
    const adjustLayerCountOnFailure = options?.adjustLayerCountOnFailure !== false;
    const currentWorkSpaceId = this.project.workSpaces[this.currentWorkSpace]
      ? this.project.workSpaces[this.currentWorkSpace].id
      : undefined;
    const load = this.buildLoadPayloadForLayerCalculate();
    const loadColor = load.color;
    const hadArrangementLayers = this.arrangementLayers.length > 0;
    const requestedSignature = this.buildDimensionsSignature(this.getLoadFormValue());
    const calculateRequest$ = this.projectApiService.calculateLayer({
      currentWorkSpaceId,
      load,
    });

    calculateRequest$.subscribe({
      next: (project) => {
        if (!project) {
          return;
        }
        this.calculatedLoadSignature = requestedSignature;
        const groups = this.getArrangementGroupsFromCalculateProject(project);
        this.arrangementLayers = this.getArrangementLayerOptions(groups, loadColor);
        this.arrangementLayerGroupId =
          resolveMatchingPalletLayerGroup(groups, loadColor)?.id ?? null;
        this.selectedPalletLayerId = null;
        this.highlightAddButton = false;
        if (this.arrangementLayers.length) {
          const firstLayer = this.arrangementLayers[0];
          this.selectedPalletLayerId = firstLayer.id;
          this.highlightAddButton = true;
          this.initArrangementLayerCounts();
          this.syncQuantityFromArrangementCounts();
          this.syncArrangementPreviewState();
          this.openTab('preview');
        } else {
          this.initArrangementLayerCounts();
        }
        const quantity = resolveSingleLayerCapacity(groups, loadColor);
        if (quantity > 0 && !this.arrangementLayers.length) {
          this.dimensionsComponent.setCalculatedQuantity(quantity);
        }
      },
      error: () =>
        this.handlePalletLayerCalculateFailure(hadArrangementLayers, adjustLayerCountOnFailure),
    });
  }

  private getArrangementGroupsFromCalculateProject(project: Project): PalletLayerGroup[] {
    const fromCurrent = getActivePalletLayerGroups(
      project,
      this.currentWorkSpace,
      this.currentStopIndex,
    );
    if (fromCurrent.length) {
      return fromCurrent;
    }
    const currentId = this.project?.workSpaces?.[this.currentWorkSpace]?.id;
    const matchedIndex =
      currentId != null
        ? project.workSpaces?.findIndex((workSpace) => workSpace.id === currentId) ?? -1
        : -1;
    if (matchedIndex >= 0) {
      const fromMatched = getActivePalletLayerGroups(project, matchedIndex, this.currentStopIndex);
      if (fromMatched.length) {
        return fromMatched;
      }
    }
    return getActivePalletLayerGroups(project, 0, -1);
  }

  private getArrangementLayerOptions(
    groups: PalletLayerGroup[],
    loadColor?: number,
  ): PalletLayer[] {
    const options = { mergeAllMatchingGroups: true, limit: 3 } as const;
    const byColor = getArrangementLayersFromGroups(groups, loadColor, options);
    if (byColor.length || loadColor == null) {
      return byColor;
    }
    // Calculate preview may omit/mismatch color — still show returned arrangements.
    return getArrangementLayersFromGroups(groups, undefined, options);
  }

  private handlePalletLayerCalculateFailure(
    hadArrangementLayers: boolean,
    adjustLayerCountOnFailure = true,
  ): void {
    if (adjustLayerCountOnFailure) {
      this.palletLayerCount = Math.max(1, this.palletLayerCount - 1);
      if (!hadArrangementLayers) {
        this.quantityLockedForLayers = false;
      }
    }
  }

  public onPalletLayerSelected(layer: PalletLayer): void {
    this.selectedPalletLayerId = layer.id;
    this.highlightAddButton = true;
    this.syncArrangementPreviewState();
  }

  public onArrangementLayerCountsChange(
    entries: { layerId: number; quantity: number }[],
  ): void {
    const next: Record<number, number> = {};
    entries.forEach((entry) => {
      next[entry.layerId] = entry.quantity >= 0 ? entry.quantity : 0;
    });
    this.arrangementLayerCounts = next;
    this.syncQuantityFromArrangementCounts();
    this.syncArrangementPreviewState();
  }

  private initArrangementLayerCounts(): void {
    const next: Record<number, number> = {};
    this.arrangementLayers.forEach((layer) => {
      next[layer.id] = 0;
    });
    this.arrangementLayerCounts = next;
  }

  /** Quantity = Σ (loads per layer type × number of layers of that type). */
  private syncQuantityFromArrangementCounts(): void {
    if (!this.showArrangementTab || !this.dimensionsComponent) {
      return;
    }
    const total = this.arrangementLayers.reduce((sum, layer) => {
      const layersCount = this.arrangementLayerCounts[layer.id] ?? 0;
      if (layersCount <= 0) {
        return sum;
      }
      return sum + (layer.quantity ?? 0) * layersCount;
    }, 0);
    this.dimensionsComponent.setCalculatedQuantity(total);
  }

  public get selectedArrangementLayer(): PalletLayer | null {
    if (this.selectedPalletLayerId == null) {
      return null;
    }
    return this.arrangementLayers.find((layer) => layer.id === this.selectedPalletLayerId) ?? null;
  }

  public syncArrangementPreviewState(): void {
    this.syncPendingLoadDimensionsForPreview();
    const nextInterlayer = this.computeArrangementPreviewInterlayer();
    const signature = this.getPalletInterlayerPreviewSignature(nextInterlayer);
    if (signature !== this.arrangementPreviewInterlayerSignature) {
      this.arrangementPreviewInterlayerSignature = signature;
      this.arrangementPreviewInterlayer = nextInterlayer;
    }
  }

  private syncPendingLoadDimensionsForPreview(): void {
    const width = this.loadData?.width;
    const length = this.loadData?.length;
    const height = this.loadData?.height;
    const signature = `${width ?? ''}|${length ?? ''}|${height ?? ''}`;
    if (signature === this.pendingLoadDimensionsSignature) {
      return;
    }
    this.pendingLoadDimensionsSignature = signature;
    this.pendingLoadDimensionsForPreview = { width, length, height };
  }

  private getPalletInterlayerPreviewSignature(interlayer: PalletInterlayer | null): string {
    if (!interlayer) {
      return '__none__';
    }
    const dimensions = interlayer.dimensions;
    return [
      interlayer.name ?? '',
      interlayer.color ?? '',
      interlayer.weight ?? '',
      interlayer.regularity ?? '',
      interlayer.onTop ? 1 : 0,
      interlayer.onBottom ? 1 : 0,
      dimensions?.width ?? '',
      dimensions?.length ?? '',
      dimensions?.height ?? '',
    ].join('|');
  }

  private computeArrangementPreviewInterlayer(): PalletInterlayer | null {
    const interlayerCfg = this.interlayersRef?.getResolvedInterlayerConfig();
    if (!interlayerCfg) {
      return null;
    }
    const cargoSpaces = getVisualizationCargoSpaces(
      this.project,
      this.currentWorkSpace,
      this.currentStopIndex,
    );
    const cargoSpace = cargoSpaces[0];
    const footprint = cargoSpace?.dimensions;
    const loadFormValue = this.dimensionsComponent?.form?.getRawValue()?.load;
    const fallbackColor = loadFormValue?.color ?? this.loadData?.color;
    const { color, weight } = resolveInterlayerTemplateMeta(
      this.interlayersRef?.getSelectedTemplateLoad(),
      fallbackColor,
    );
    return interlayerConfigToPalletInterlayer(
      interlayerCfg,
      color,
      weight,
      footprint?.width && footprint?.length
        ? { width: footprint.width, length: footprint.length }
        : undefined,
    );
  }

  private syncArrangementLayerCountsFromForm(): void {
    const entries = this.arrangementRef?.getLayerCounts();
    if (!entries?.length) {
      return;
    }
    this.onArrangementLayerCountsChange(entries);
  }

  private buildLoadPayloadForLayerCalculate(): Load {
    const addLoad: Partial<Load> = this.prepareAddLoad();
    const loadFormValue = this.getLoadFormValue();
    let convertedLoad: Load = addLoad as Load;
    const isMm = this.project.options.unit === 'mm';
    if (isMm) {
      convertedLoad = convertLoadsDimensions([convertedLoad], false)[0];
    }
    convertedLoad.color = loadFormValue.color
      ? loadFormValue.color
      : this.colorsService.getColor();
    const formQty = loadFormValue.quantity;
    const parsedQty = parseFloat(String(formQty ?? ''));
    convertedLoad.quantity = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
    return convertedLoad;
  }

  private addLoadBody(toFirstEmpty: boolean): void {
    const loadFormValue = this.getLoadFormValue();
    if (this.showArrangementTab) {
      this.syncArrangementLayerCountsFromForm();
      if (this.arrangementRef?.hasLayersToAddValidationErrors()) {
        this.arrangementRef.triggerLayersToAddValidation();
        this.openTab('preview');
        return;
      }
    }
    if (
      this.showArrangementTab &&
      this.arrangementLayers.length &&
      this.selectedPalletLayerId == null
    ) {
      this.openTab('preview');
      return;
    }
    const isLoadLimitReached: boolean =
      this.projectService.currentNumberOfLoads +
        parseFloat(loadFormValue.quantity as unknown as string) >
      this.projectService.maxNumberOfLoads;
    if (isLoadLimitReached) {
      this.modalService.open(MaxLoadsPopupComponent, null).subscribe();
      return;
    }
    this.dimensionsComponent.formEl.onSubmit(undefined);
    if (this.interlayersRef?.form?.dirty && this.interlayersRef.hasValidationErrors()) {
      this.interlayersRef.triggerValidation();
      this.openTab('interlayers');
      return;
    }
    const currentWorkSpaceId = this.project.workSpaces[this.currentWorkSpace]
      ? this.project.workSpaces[this.currentWorkSpace].id
      : undefined;
    const addLoad: Partial<Load> = this.prepareAddLoad();
    let convertedLoad: Load = addLoad as Load;
    const isMm = this.project.options.unit === 'mm';
    if (isMm) {
      convertedLoad = convertLoadsDimensions([convertedLoad], false)[0];
    }
    const rawInterlayer = this.interlayersRef?.getResolvedInterlayerConfig();
    const interlayerCfg =
      rawInterlayer && isMm
        ? { ...rawInterlayer, thickness: rawInterlayer.thickness / 10 }
        : rawInterlayer;
    const loadColor = loadFormValue.color
      ? loadFormValue.color
      : this.colorsService.getColor();
    if (this.dimensionsComponent.form.disabled || this.isFormValid()) {
      convertedLoad.color = loadColor;
      const layerEditRequest = this.buildPalletLayerGroupEditForRequest(
        interlayerCfg,
        loadColor,
        currentWorkSpaceId,
      );
      if (!layerEditRequest && this.shouldBlockIndividualAddPath()) {
        return;
      }
      const addRequest$ = layerEditRequest
        ? this.projectApiService.editPalletLayerGroup(layerEditRequest)
        : this.projectApiService.addGroup({
            currentWorkSpaceId: currentWorkSpaceId,
            newLoads: [
              {
                quantity: loadFormValue.quantity,
                load: convertedLoad,
              },
            ],
            workSpaceId: toFirstEmpty ? undefined : currentWorkSpaceId,
            currentStopId:
              this.currentStopIndex >= 0
                ? this.project.workSpaces[this.currentWorkSpace]?.stops[this.currentStopIndex]?.id
                : undefined,
          });

      addRequest$.subscribe((result: Project): void => {
        if (
          this.dimensionsComponent.saveLoadForm.value.remember &&
          this.dimensionsComponent.checkboxActive
        ) {
          this.dimensionsComponent.checkboxActive = false;
          this.dimensionsComponent.saveLoadForm.controls.remember.setValue(false);
          this.userApiService.saveLoad(convertedLoad).subscribe();
        }
        if (this.interlayersRef?.shouldRememberSave() && interlayerCfg) {
          this.interlayersApi
            .createSavedInterlayer(this.buildInterlayerSavePayload(interlayerCfg))
            .subscribe(() => {
              this.interlayersRef?.form.controls.remember.setValue(false);
            });
        }
        if (result) {
          if (!layerEditRequest) {
            this.resetForm(true);
            this.dimensionsComponent.checkboxActive = false;
          }
          this.notificationsService.generateCOGNotification(result, toFirstEmpty);
          void this.notificationsService.generateLoadNotDisplayedNotification(
            convertedLoad,
            result,
          );
        }
        const handleProjectAfterAdd = (project: Project): void => {
          this.project = project;
          if (layerEditRequest) {
            this.resetForm(true);
            this.closeAddLoad.emit();
          } else {
            this.onAddLoad(this.dimensionsComponent.chosenLoad);
          }
        };
        if (result?.workSpaces) {
          handleProjectAfterAdd(result);
        } else {
          this.projectService.project.pipe(take(1)).subscribe(handleProjectAfterAdd);
        }
      });
    }
  }

  public onSearch(value: string): void {
    this.currentSearchString = value || '';
    this.loadLoads();
    if (value) {
      this.loads = this.loads.filter((load: LoadWithTranslatedName) =>
        load.translatedName.toLowerCase().includes(value.toLowerCase()),
      );
    }
  }

  public onFormData(formData: FormDataType): void {
    setTimeout(() => {
      if (
        (formData.width && formData.height && formData.length) ||
        (formData.diameter && formData.height) ||
        (formData.diameter && formData.length)
      ) {
        this.loadData = {
          ...convertLoadDimensions({ ...formData } as FormDataType, this.project.options.unit),
          centerOfMass: this.centerOfMassValue ?? this.centerOfMass,
        };
        this.loadDataVis = {
          ...this.loadData,
          centerOfMass: undefined,
        };
      } else {
        this.loadData = convertLoadDimensions(this.getDefaultLoadData(), this.project.options.unit);
      }
      this.syncArrangementPreviewState();
      if (!supportsPalletLayerLoadType(this.loadData?.loadType) && this.isTabInterlayers) {
        this.openTab('preview');
      }
    }, 0);
  }

  public sortByNameSwitch(): void {
    this.isSortByNameEnabled = !this.isSortByNameEnabled;
    this.cookieService.get('sortByName').includes('true')
      ? this.cookieService.set('sortByName', 'false', undefined, '/', null, false, 'Lax')
      : this.cookieService.set('sortByName', 'true', undefined, '/', null, false, 'Lax');
    this.loadLoads();
  }

  public isFormDisabled(): boolean {
    return this.dimensionsComponent.form.disabled;
  }

  public resetForm(withDisabled?: boolean): void {
    this.isQuick = false;
    this.loadData = convertLoadDimensions(this.getDefaultLoadData(), this.project.options.unit);
    this.loadDataVis = this.loadData;
    this.currentLoad = this.getDefaultLoad();
    // Don't clear currentType if we have a saved load - it's needed for form fields visibility
    // savedLoad is preserved for restore functionality via initialDimensions()
    this.nameToSend = undefined;
    this.stackingOptions = undefined;
    this.centerOfMassValue = undefined;
    this.rotateOptions = {
      rotateOnBase: null,
      rotateFreely: null,
      allowToRotate: null,
    };
    this.loadingPlacesOptions = undefined;
    this.centerOfMass = undefined;
    this.palletLayerCount = 1;
    this.clearArrangementState();
    if (!withDisabled && this.isFormDisabled()) {
      this.dimensionsComponent.enableFormSafely();
    }
    this.dimensionsComponent.formEl.resetForm();
    this.interlayersRef?.reset();
    this.syncArrangementPreviewState();
  }

  private buildInterlayerSavePayload(cfg: InterlayerConfig): InterlayerWritePayload {
    const loadFormValue = this.dimensionsComponent.form.getRawValue().load;
    const fallbackColor = loadFormValue?.color ?? this.loadData?.color;
    const { color, weight } = resolveInterlayerTemplateMeta(
      this.interlayersRef?.getSelectedTemplateLoad(),
      fallbackColor ?? this.colorsService.getAvaliableColor(),
    );
    return {
      name: cfg.name,
      thickness: cfg.thickness,
      weight,
      color,
    };
  }

  private mapSavedInterlayerToLoad(interlayer: SavedInterlayer): LoadWithTranslatedName {
    return {
      id: interlayer.id,
      name: interlayer.name,
      translatedName: this.translateLoadName(interlayer.name),
      loadType: 0,
      length: 0,
      width: 0,
      height: interlayer.thickness,
      weight: interlayer.weight ?? 0,
      stacking: false,
      show: true,
      stackingOptions: { interlayer: true },
      color: interlayer.color ?? this.colorsService.getAvaliableColor(),
    };
  }

  public editLoad(load: Load): void {
    if (this.isQuickMode && this.currentLoad.id === load.id) {
      this.isQuick = !this.isQuick;
    } else if (this.isQuickMode && this.isQuick && this.currentLoad.id !== load.id) {
      this.isQuick = false;
    }
    this.onChosen(load, false);

    if (this.isFormDisabled()) {
      this.dimensionsComponent.enableFormSafely();
    }

    this.isListVisible = false;
  }

  public initialDimensions(): void {
    // Check if we have a saved load and restore it
    if (this.savedLoad) {
      // Restore currentType from saved load to ensure form fields are displayed correctly
      if (this.savedLoad.loadType !== undefined) {
        this.currentType = this.savedLoad.loadType;
      }
      // Restore the saved load
      this.onChosen(this.savedLoad, false);
    } else if (this.currentLoad) {
      // Fallback to currentLoad if no saved load
      this.onChosen(this.currentLoad, false);
    }
  }

  public openTab(tabName: string): void {
    this.isTabStacking = false;
    this.isTabRotate = false;
    this.isTabGravityOfCenter = false;
    this.isTabLoadingPlaces = false;
    this.isTabInterlayers = false;
    this.isTabArrangement = false;
    this.isTabPreview = false;
    switch (tabName) {
      case 'stacking':
        this.isTabStacking = true;
        break;
      case 'preview':
        this.isTabPreview = true;
        break;
      case 'rotate':
        this.isTabRotate = true;
        break;
      case 'gravityOfCenter':
        this.isTabGravityOfCenter = true;
        break;
      case 'loadingPlaces':
        this.isTabLoadingPlaces = true;
        break;
      case 'interlayers':
        this.isTabInterlayers = true;
        break;
      case 'arrangement':
        this.isTabArrangement = true;
        break;
    }
  }

  public trackByFunction(_index, item: Load): string {
    return `${item.id}${item.name}${item.loadType}${item.width}${item.height}${item.length}${item.diameter}`;
    // return JSON.stringify(item);
  }

  private getDefaultLoad(): Load {
    return {
      color: this.colorsService.getAvaliableColor(),
      height: null,
      length: null,
      name: '',
      quantity: null,
      weight: null,
      stacking: null,
      show: null,
      width: null,
      loadType: null,
      diameter: null,
      centerOfMass: null,
    };
  }

  private getDefaultLoadData(): FormDataType {
    return {
      color: this.colorsService.getAvaliableColor(),
      height: null,
      length: null,
      name: '',
      nameTranslated: '',
      quantity: 1,
      sumOfWeights: 0,
      weight: 0,
      width: null,
      loadType: this.currentType,
      centerOfMass: null,
      diameter: null,
    };
  }

  private loadLoads() {
    this.loads = this.sortedLoads?.map((load: Load) => {
      return {
        ...load,
        translatedName: this.translateLoadName(load.name),
        stackingOptions: {
          ...load.stackingOptions,
        },
      };
    });
    if (this.isSortByNameEnabled) {
      this.loads = this.userService.sortByTranslatedName(this.loads) as LoadWithTranslatedName[];
    }
    this.refreshInterlayerSavedLoads();
  }

  private translateLoadName(loadName: string): string {
    if (!loadName) {
      return loadName;
    }
    const display = resolveInterlayerDisplayName(loadName, (key, params) =>
      String(this.translate.instant(key, params)),
    );
    if (display) {
      return display;
    }
    return String(this.translate.instant(loadName));
  }

  private getLoadFormValue(): FormDataType {
    const rawValue = this.dimensionsComponent.form.getRawValue() as { load: FormDataType };
    return rawValue.load;
  }

  /** Dimensions needed to calculate a pallet layer — quantity is how many to add, not a calc input. */
  private hasValidLayerCalculateDimensions(): boolean {
    const loadFormValue = this.getLoadFormValue();
    if (this.currentType === 1) {
      return !!(loadFormValue.height && loadFormValue.diameter);
    }
    if (this.currentType === 2) {
      return !!(loadFormValue.length && loadFormValue.diameter);
    }
    return !!(loadFormValue.length && loadFormValue.width && loadFormValue.height);
  }

  private isFormValid() {
    return this.hasValidLayerCalculateDimensions() && !!this.getLoadFormValue().quantity;
  }
}
